/**
 * The typed client for fleet-api, the CI fleet screen's backend (org-admin#96).
 *
 * ── A DIFFERENT CREDENTIAL FROM EVERY OTHER CALL IN THIS APP ─────────
 *
 * Everything in `api.ts` rides the admin session cookie. fleet-api refuses
 * cookies by design: it verifies a bearer access token whose audience is
 * fleet-api's OWN (`<issuer>/app/fleet-api`), which the auth service will mint
 * for this portal only because RidgeWill/auth#476 declared the grant. So this
 * client sends `Authorization: Bearer` and `credentials: 'omit'`.
 *
 * Where the token comes from is `fleet-token.ts`. This file only spends it, and
 * takes it as a function so a test can hand it one without a sign-in.
 *
 * ── EVERY REFUSAL IS A 404 THERE ─────────────────────────────────────
 *
 * fleet-api answers a missing token, an expired one, a token for another
 * audience and a signed-in non-staff caller identically: 404, empty body. So
 * this client cannot say which it was, and the message says so rather than
 * guessing. Its `/ready` and the Worker's logs are where the reason is.
 */

export const FLEET_URL = (import.meta.env.VITE_FLEET_URL ?? '').replace(/\/$/, '');

if (!FLEET_URL) {
  // Loud at load, like VITE_AUTH_URL: a blank base sends every request to this
  // app's own origin, which the static host answers with HTML.
  console.error('[org-admin] VITE_FLEET_URL is not set: the CI fleet screen cannot reach fleet-api');
}

/** Hands back a current access token for fleet-api, fetching one if needed. */
export type TokenSource = () => Promise<string>;

export class FleetError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'FleetError';
  }
}

export const REFUSED_MESSAGE =
  'fleet-api refused the request (404). It answers every refusal the same way, so this is one of: no token, an expired token, a token for the wrong audience, or an account without a staff role.';

export async function fleetGet<T>(path: string, token: TokenSource, fetchImpl: typeof fetch = fetch): Promise<T> {
  const res = await fetchImpl(`${FLEET_URL}${path}`, {
    headers: { Authorization: `Bearer ${await token()}` },
    // Never the cookie. fleet-api sends no Allow-Credentials, so a credentialed
    // request would fail CORS outright, and it has no use for it anyway.
    credentials: 'omit',
  });
  if (!res.ok) {
    throw new FleetError(res.status, res.status === 404 ? REFUSED_MESSAGE : `fleet-api answered ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

/**
 * The writes: moving CI by hand (org-admin#98), and queueing or cancelling a
 * box action (org-admin#99).
 *
 * A 404 is still a refusal nobody can explain. Anything else fleet-api refuses
 * (a label outside the chain, a change that raced another, GitHub refusing the
 * write, a box that already has an action in flight) arrives as `{ error }`
 * written for a person, and is shown as it is.
 */
export async function fleetPost<T>(path: string, body: unknown, token: TokenSource, fetchImpl: typeof fetch = fetch): Promise<T> {
  const res = await fetchImpl(`${FLEET_URL}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${await token()}`, 'Content-Type': 'application/json' },
    credentials: 'omit',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 404) throw new FleetError(404, REFUSED_MESSAGE);
    const reason = await res
      .json()
      .then((b: { error?: unknown }) => (typeof b.error === 'string' ? b.error : null))
      .catch(() => null);
    throw new FleetError(res.status, reason ?? `fleet-api answered ${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

// ── RESPONSE SHAPES, as fleet-api#27, #28, #30, #31 and #32 describe them ─

export type Liveness = 'online' | 'disputed' | 'unknown';

export interface Runner {
  name: string;
  kind: string;
  labels: string[];
  liveness: Liveness;
  /** GitHub's word, advisory only (actions/runner#3892). */
  github_status: string;
  github_busy: boolean;
  last_job_at: string | null;
  last_seen_at: string | null;
  updated_at: string;
}

export interface Summary {
  runs: number;
  median_s: number | null;
  p90_s: number | null;
}

export interface JobKey {
  repo: string;
  workflow: string;
  job_name: string;
}

export interface HistoryDay extends Summary {
  day: string;
  kind: string;
  queue_median_s: number | null;
}

export interface JobHistory extends JobKey, Summary {
  days: HistoryDay[];
}

export interface Baseline {
  repo: string;
  job: string;
  stat: 'median' | 'p90';
  seconds: number;
  label: string;
}

export interface History {
  since: string;
  jobs: JobHistory[];
  baselines: Baseline[];
}

export interface KindSummary extends Summary {
  kind: string;
}

export interface Steps {
  since: string;
  job: JobKey | null;
  by_kind: KindSummary[];
  steps: { number: number; name: string; by_kind: KindSummary[] }[];
  boxes: (Summary & { runner_name: string })[];
}

export interface BusyHour {
  hour: string;
  busy_runner_s: number;
  peak: number;
}

export interface Capacity {
  since: string;
  runners: number;
  queue: { jobs: number; p50_s: number | null; p95_s: number | null; max_s: number | null };
  concurrency: {
    window_s: number;
    busy_s: number;
    all_busy_s: number;
    busy_share: number;
    all_busy_share: number | null;
    peak: number;
    p95_while_busy: number | null;
  };
  hours: BusyHour[];
}

/** A run-history job, so its details dialog opens from the queue too (org-admin#126). */
export interface WaitingJob extends RunJob {
  labels: string[];
  waiting_s: number;
  for_our_runners: boolean;
  matching_runners: { name: string; idle: boolean }[];
  stuck: boolean;
}

export interface Queue {
  as_of: string;
  last_tick_at: string | null;
  jobs: WaitingJob[];
}

export interface Cost {
  month: string;
  as_of: string;
  elapsed_share: number;
  currency: 'USD';
  minutes: { kind: string; run_min: number; billable_min: number }[];
  blacksmith: {
    used_min: number;
    projected_min: number;
    free_min: number;
    trip_min: number;
    bill_to_date: number;
    bill_projected: number;
  };
  fleet: { billed_equivalent_min: number; projected_min: number };
  /** From GitHub's bill once fleet-api has read it for the month, else counted from collected jobs (fleet-api#21). */
  github_hosted: {
    billable_min: number;
    projected_min: number;
    source: 'billing' | 'jobs';
    included_min: number | null;
    net_min: number | null;
    net_usd: number | null;
  };
  avoided: { to_date: number; projected: number };
  electricity: { kwh_to_date: number; kwh_projected: number; to_date: number; projected: number };
  net: { to_date: number; projected: number };
  /**
   * The window the rest of the month is projected from: the last 7 days, or the
   * month so far early in a month. Null for a past month. Optional until every
   * fleet-api answering the portal has it.
   */
  projection?: { from: string; days: number } | null;
  assumptions: {
    blacksmith_usd_per_min: number;
    electricity_usd_per_kwh: number;
    avg_watts_per_box: number;
    boxes: number;
  };
}

/** A job that has started on a runner and not finished, per `jobs` rather than
 *  GitHub's busy flag (actions/runner#3892). A run-history job, so its details
 *  dialog opens from the runner board too (org-admin#126). */
export interface RunningJob extends RunJob {
  runner_name: string;
  started_at: string;
  elapsed_s: number;
}

export interface FleetStatus {
  as_of: string;
  /** Sent raw so `tickView` alone decides what they mean. */
  collector: {
    last_tick_at: string | null;
    last_tick_attempt_at: string | null;
    last_tick_error: string | null;
  };
  backfill: {
    running: boolean;
    until: string | null;
    repo_days_done: number;
    completed_at: string | null;
  };
  running: RunningJob[];
}

/** One job in the run-history feed, with its run's branch and trigger (fleet-api `/v1/fleet/runs`, org-admin#111). */
export interface RunJob {
  id: number;
  run_id: number;
  run_attempt: number;
  repo: string;
  workflow: string;
  job_name: string;
  runner_name: string | null;
  kind: string | null;
  status: string;
  conclusion: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  duration_s: number | null;
  queue_s: number | null;
  /** Null for runs the collector listed before it kept these. */
  head_branch: string | null;
  event: string | null;
  actor: string | null;
  title: string | null;
}

export interface RunsFeed {
  since: string;
  until: string;
  /** More jobs than fleet-api sends in one window; the oldest were left out. */
  truncated: boolean;
  jobs: RunJob[];
}

/** One job in the Job analysis picker (fleet-api `/v1/fleet/jobs`, org-admin#111). `runs`, median and p90 are passing runs only. */
export interface JobIndexEntry extends JobKey, Summary {
  /** Every run in the window, whatever its outcome. */
  total: number;
  failed: number;
  last_run_at: string;
  /** Whether the step breakdown will have anything in it. Steps are kept for test jobs only. */
  has_steps: boolean;
}

export interface JobIndex {
  since: string;
  jobs: JobIndexEntry[];
}

export type RoutingMode = 'off' | 'dry_run' | 'auto';

export interface RoutingAuditRow {
  id: number;
  at: string;
  /** 'fleet-api' for the cron, 'github' for a change seen from outside, or an admin's id. */
  actor: string;
  /**
   * `cancel` and `rerun`: a run a failover left waiting on the old label (fleet-api#38).
   * `action_*`: a box action requested, claimed by its box, and its result (fleet-api#24),
   * with `{action_id, box, verb, target}` in `inputs`.
   */
  action:
    | 'switch'
    | 'would_switch'
    | 'external_change'
    | 'refused'
    | 'error'
    | 'cancel'
    | 'rerun'
    | 'action_requested'
    | 'action_claimed'
    | 'action_result';
  from_label: string | null;
  to_label: string | null;
  reason: string;
  inputs: unknown;
  /**
   * For a switch: whether reading the variable back matched. For a rerun: whether
   * a new attempt of the run was seen, and null when it was not re-run because
   * nothing in it failed.
   */
  verified: boolean | null;
}

/** GitHub-hosted Actions minutes this month, from GitHub's bill (fleet-api#21). */
export interface GitHubMinutes {
  month: string;
  gross_min: number;
  included_min: number;
  net_min: number;
  net_usd: number;
  price_per_min: number | null;
  /** The organisation's Actions budget in USD, and whether GitHub stops Actions when it is spent. */
  budget_usd: number | null;
  budget_stops_usage: boolean | null;
  as_of: string;
}

/** Where CI runs and why (fleet-api `/v1/fleet/routing`, org-admin#98). */
export interface Routing {
  as_of: string;
  mode: RoutingMode;
  /** Why routing cannot act on its settings, or null. */
  problem: string | null;
  chain: { fleet: string; blacksmith: string; github: string };
  failover_after_s: number;
  failback_after_s: number;
  state: {
    /** CI_RUNNER_LABEL as fleet-api last read or wrote it. */
    label: string | null;
    paused_reason: string | null;
    switched_at: string | null;
    switched_by: string | null;
    failed_over_at: string | null;
    healthy_since: string | null;
    trials: number;
    next_trial_at: string | null;
    updated_at: string;
  };
  /** `free_min` is Blacksmith's free allowance; `trip_min` is where routing stops choosing it. */
  blacksmith: { used_min: number; trip_min: number; free_min: number };
  github_hosted: GitHubMinutes | null;
  /** The most GitHub-hosted runners can use this month: included minutes plus what a stopping budget buys. */
  github_limit: { included_min: number; budget_usd: number | null; budget_stops_usage: boolean | null; cap_min: number | null };
  last_routing_error: string | null;
  /**
   * The low-churn repos' label, CI_HOSTED_LABEL (fleet-api#49): what the month's
   * Blacksmith minutes last moved it to, and when. `label` is null before the
   * first crossing. Optional until every fleet-api answering the portal has it.
   */
  hosted?: { variable: string; label: string | null; set_at: string | null };
  /** Newest first, at most 50. */
  audit: RoutingAuditRow[];
}

/** The endpoints, bound to one token source. */
/** fleet-api's reading of a CI box (fleet-api#22): `silent` after `silent_after_s` without a report. */
export type BoxState = 'reporting' | 'silent' | 'never_reported' | 'revoked';

/** Derived by fleet-api from the box's own report, not from GitHub's status. */
export type Listener = 'connected' | 'no_connection' | 'not_running' | 'unknown';

export interface BoxRunner {
  /** The systemd unit, e.g. `ridgewill-runner@headless-1-a.service`. */
  unit: string;
  active_state: string | null;
  sub_state: string | null;
  /** Climbs with every job, because an ephemeral runner restarts after each one. */
  restarts: number | null;
  github_connections: number | null;
  agent_id: number | null;
  last_line: string | null;
  last_line_at: string | null;
  listener: Listener;
}

export interface BoxReading {
  received_at: string;
  hostname: string | null;
  uptime_s: number | null;
  cpu_temp_c: number | null;
  load_1: number | null;
  load_5: number | null;
  load_15: number | null;
  mem_used_mb: number | null;
  mem_total_mb: number | null;
  disk_used_gb: number | null;
  disk_total_gb: number | null;
  docker_gb: number | null;
}

export interface Box {
  name: string;
  state: BoxState;
  registered_at: string;
  revoked_at: string | null;
  last_report_at: string | null;
  since_report_s: number | null;
  /** The latest readings. Only current when `state` is `reporting`. */
  last_report: (BoxReading & { runners: BoxRunner[] }) | null;
  history: { at: string; cpu_temp_c: number | null; load_1: number | null; mem_used_mb: number | null; disk_used_gb: number | null; docker_gb: number | null }[];
  /** The latest box actions, newest first. Optional until every fleet-api answering the portal has them. */
  actions?: BoxAction[];
}

/** The fixed list a box's agent runs (fleet-api#24). Nothing sent to a box is ever a command string. */
export type BoxVerb = 'report' | 'restart_runner' | 'prune_docker' | 'reboot';

export type BoxActionStatus = 'queued' | 'claimed' | 'succeeded' | 'failed' | 'expired' | 'cancelled';

export interface BoxAction {
  id: number;
  box: string;
  verb: BoxVerb;
  /** The runner, for `restart_runner`; null for every other verb. */
  target: string | null;
  status: BoxActionStatus;
  /** The admin's id. */
  requested_by: string;
  requested_at: string;
  claimed_at: string | null;
  completed_at: string | null;
  /** The tail of what the action printed, or fleet-api's reason for an expiry, timeout or cancel. */
  output: string | null;
}

/** A spell on battery (fleet-api#59). Opened by the first report on battery, closed by the first back on line. */
export interface UpsOutage {
  id: number;
  started_at: string;
  last_on_battery_at: string;
  /** When fleet-api next heard the UPS on line: the power came back between `last_on_battery_at` and this. Null while open. */
  ended_at: string | null;
  /** Start to the last report on battery: the shortest the outage can have been. */
  on_battery_s: number;
  min_charge_pct: number | null;
  min_runtime_s: number | null;
  /** It reached the line the shutdown policy acts on. */
  low_battery: boolean;
}

/** The UPS behind the boxes, as the box with its USB cable reports it (fleet-api#59). */
export interface Ups {
  /** NUT's name for it, e.g. `cyberpower`. */
  name: string;
  /** The box that reads it. */
  box: string;
  model: string | null;
  state: 'reporting' | 'silent';
  last_report_at: string;
  since_report_s: number;
  /** The latest readings. Only current when `state` is `reporting`. */
  last: {
    status: string;
    flags: string[];
    on_battery: boolean;
    low_battery: boolean;
    charge_pct: number | null;
    runtime_s: number | null;
    load_pct: number | null;
    nominal_w: number | null;
    /** Load times nominal, so 10 W steps on a 1000 W UPS. */
    watts: number | null;
    input_v: number | null;
    charge_low_pct: number | null;
    runtime_low_s: number | null;
  };
  /** Newest first. */
  outages: UpsOutage[];
}

export interface Boxes {
  as_of: string;
  silent_after_s: number;
  hours: number;
  boxes: Box[];
  /** Optional until every fleet-api answering the portal sends it (fleet-api#59). */
  ups?: Ups[];
}

export function fleetApi(token: TokenSource, fetchImpl: typeof fetch = fetch) {
  const get = <T>(path: string) => fleetGet<T>(path, token, fetchImpl);
  const post = <T>(path: string, body: unknown) => fleetPost<T>(path, body, token, fetchImpl);
  return {
    runners: () => get<{ runners: Runner[] }>('/v1/runners'),
    // Each CI box's health from its own agent (fleet-api#22, docs#67).
    boxes: (hours = 1) => get<Boxes>(`/v1/fleet/boxes?hours=${hours}`),
    queue: () => get<Queue>('/v1/fleet/queue'),
    status: () => get<FleetStatus>('/v1/fleet/status'),
    runs: (hours = 24) => get<RunsFeed>(`/v1/fleet/runs?hours=${hours}`),
    history: (days = 30) => get<History>(`/v1/fleet/history?days=${days}`),
    // With no job named, fleet-api picks the slowest test job itself.
    steps: (days = 30, job?: JobKey) =>
      get<Steps>(
        `/v1/fleet/steps?${new URLSearchParams({
          days: String(days),
          ...(job ? { repo: job.repo, workflow: job.workflow, job: job.job_name } : {}),
        })}`,
      ),
    jobs: (days = 30) => get<JobIndex>(`/v1/fleet/jobs?days=${days}`),
    capacity: (days = 30) => get<Capacity>(`/v1/fleet/capacity?days=${days}`),
    cost: (month?: string) => get<Cost>(`/v1/fleet/cost${month ? `?month=${encodeURIComponent(month)}` : ''}`),
    routing: () => get<Routing>('/v1/fleet/routing'),
    // Answers the routing view as it stands after the switch.
    setRouting: (label: string) => post<Routing>('/v1/fleet/routing', { label }),
    // Queue an action for a box's next report, and cancel one no box has claimed (org-admin#99).
    requestBoxAction: (box: string, verb: BoxVerb, target?: string) =>
      post<{ action: BoxAction }>('/v1/fleet/boxes/actions', target === undefined ? { box, verb } : { box, verb, target }).then((r) => r.action),
    cancelBoxAction: (id: number) => post<{ action: BoxAction }>(`/v1/fleet/boxes/actions/${id}/cancel`, {}).then((r) => r.action),
  };
}
